import { useState } from 'react';
import { Filter, X, Clock } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { WebhookEventsQuery } from '../../types';

// Time range presets (Kibana-style)
const TIME_PRESETS = [
  { label: '15 min', value: '15m', minutes: 15 },
  { label: '30 min', value: '30m', minutes: 30 },
  { label: '1 hora', value: '1h', minutes: 60 },
  { label: '4 horas', value: '4h', minutes: 240 },
  { label: '24 horas', value: '24h', minutes: 1440 },
  { label: '7 dias', value: '7d', minutes: 10080 },
] as const;

interface WebhookFiltersProps {
  onFiltersChange: (filters: WebhookEventsQuery) => void;
}

export function WebhookFilters({ onFiltersChange }: WebhookFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<WebhookEventsQuery>({});
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  const handleFilterChange = (key: keyof WebhookEventsQuery, value: string | undefined) => {
    const newFilters = { ...filters, [key]: value || undefined };
    setFilters(newFilters);
  };

  const handleTimePreset = (preset: typeof TIME_PRESETS[number]) => {
    const now = new Date();
    const from = new Date(now.getTime() - preset.minutes * 60 * 1000);
    const newFilters = {
      ...filters,
      fromDate: from.toISOString(),
      toDate: now.toISOString(),
    };
    setFilters(newFilters);
    setSelectedPreset(preset.value);
  };

  const handleApplyFilters = () => {
    onFiltersChange(filters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    setSelectedPreset(null);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <div className="space-y-4">
      {/* Filters toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={isOpen ? 'default' : 'outline'}
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filtros
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-primary-foreground text-primary text-xs">
              {Object.values(filters).filter((v) => v !== undefined).length}
            </span>
          )}
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Filters panel */}
      {isOpen && (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          {/* Sender ID with match type */}
          <div className="space-y-2">
            <Label>Remetente</Label>
            <div className="flex gap-2">
              <Select
                value={filters.senderIdMatchType || 'contains'}
                onValueChange={(value) => handleFilterChange('senderIdMatchType', value as 'equals' | 'contains')}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">Contém</SelectItem>
                  <SelectItem value="equals">Igual</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="ID do remetente..."
                value={filters.senderId || ''}
                onChange={(e) => handleFilterChange('senderId', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="processing">Processando</SelectItem>
                <SelectItem value="processed">Processado</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time range presets (Kibana-style) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-3 w-3" />
              Período
            </Label>
            <div className="flex flex-wrap gap-2">
              {TIME_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  variant={selectedPreset === preset.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTimePreset(preset)}
                  className="text-xs"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Apply button */}
          <Button onClick={handleApplyFilters} className="w-full">
            Aplicar filtros
          </Button>
        </div>
      )}
    </div>
  );
}
